/**
 * Hook for reacting to store code changes
 *
 * This hook subscribes to store code change events and handles
 * clearing/reloading cart data when the store code changes.
 *
 * When user selects a new region with different store code:
 * 1. Cart data should be cleared (cart is specific to store)
 * 2. Cart item references should be cleared
 * 3. Draft cart should be cleared
 *
 * USAGE:
 * Place this hook in a component that is always mounted when the module is active,
 * typically in the module's main provider or navigator.
 *
 * ```typescript
 * // In QuoteProvider or CartProvider
 * export const QuoteProvider = ({children}) => {
 *     useQuoteStoreCodeChange();
 *     return <>{children}</>;
 * };
 * ```
 *
 * @see mobile: module-quote/src/hooks/use-quote-store-code-change.ts
 */

'use client';

import { useEffect } from 'react';
import { storeCodeEmitter, type StoreCodeChangePayload } from '@kidslabs/module-core';
import { cartStoreAPI } from '../stores';
import { clearAllQuoteStores } from '../stores/clear-all-quote-stores';
import { cartSessionManager, storeChangeResetRegistry } from '../contracts';
import { setInternalUpdate, updateLastSyncedState, lockSync } from '../stores/cart-item-reference.store';

const MODULE_ID = 'quote';

/**
 * Handler for store code change events.
 * Clears ALL cart-related data via Legend-State's own .set() API,
 * which automatically persists the reset state to IndexedDB.
 *
 * Flow:
 * 1. Block sync to prevent intermediate state triggering server calls
 * 2. Reset all quote stores via Legend-State .set(initialState)
 *    → Legend-State handles IndexedDB persistence automatically
 * 3. Reset checkout & giftSelection stores (they don't have own handlers)
 * 4. Check session for new store's existing cart
 * 5. Page reloads → hydrates from IndexedDB with clean state
 */
const handleStoreCodeChanged = async (payload: StoreCodeChangePayload): Promise<void> => {
    console.log('[QuoteModule] Store code changed:', {
        from: payload.previousStoreCode,
        to: payload.newStoreCode,
    });

    try {
        console.log('[QuoteModule] Clearing all cart data for store code change');

        // CRITICAL: Lock sync FIRST to prevent any outbound sync of empty data.
        // This is explicitly called even though sync is locked by default on page load,
        // because if user switches store within the same session (no reload yet),
        // sync may have been unlocked by a previous fetchCartItemInternal.
        lockSync();

        // CRITICAL: Block sync for the ENTIRE duration of store change.
        setInternalUpdate(true);

        // 1. Clear ALL quote/cart stores via Legend-State .set(initialState)
        //    Legend-State handles IndexedDB persistence automatically — no manual
        //    IndexedDB manipulation needed. Each store$.set(initial) triggers
        //    the persistence plugin to write the clean state to IndexedDB.
        clearAllQuoteStores();
        updateLastSyncedState([]);

        // 2. Reset provider lifecycle flags so auth effect re-runs for new store
        cartStoreAPI.actions.resetCartForStoreChange();

        // 3. Execute all registered external module reset handlers
        //    (checkout, flash-sale, etc. register via storeChangeResetRegistry)
        await storeChangeResetRegistry.executeAll();

        // 4. Check if the NEW store already has a cart in session
        if (cartSessionManager.hasAdapter() && payload.newStoreCode) {
            const newStoreSession = await cartSessionManager.getCartId(payload.newStoreCode);
            if (newStoreSession.cartId) {
                console.log('[QuoteModule] Found existing cart for new store:', newStoreSession.cartId);
                cartStoreAPI.actions.setCartId(newStoreSession.cartId);
            }
        }

        console.log('[QuoteModule] Cart data cleared for store code change');

        // CRITICAL: Reset internal update flag so sync is not permanently blocked.
        // Previously this was left as `true` assuming a page reload would reset it,
        // but in SPA navigation the module may not remount, causing sync to be
        // blocked forever and cart data to appear "lost".
        // The lockSync() above already prevents outbound sync of empty data;
        // setInternalUpdate(false) just re-enables the onChange handler so that
        // future user-driven changes (after fetch + unlockSync) can trigger sync.
        setInternalUpdate(false);
    } catch (error) {
        console.error('[QuoteModule] Error handling store code change:', error);
        setInternalUpdate(false);
    }
};

/**
 * Hook to subscribe to store code change events
 *
 * This hook should be called in a component that is always mounted
 * when the quote module is in use (e.g., in the module's provider).
 */
export const useQuoteStoreCodeChange = (): void => {
    useEffect(() => {
        storeCodeEmitter.onStoreCodeChanged(MODULE_ID, handleStoreCodeChanged);

        console.log('[QuoteModule] Store code change listener registered');

        return () => {
            storeCodeEmitter.unregister(MODULE_ID);
            console.log('[QuoteModule] Store code change listener unregistered');
        };
    }, []);
};

// Export the handler for direct usage if needed
export { handleStoreCodeChanged };
